import { getCookie } from '../../helpers/cookieMetods';
import type { EntityId } from '../types/common';
import type { NewsPost } from '../types/news';
import { JSON_POST_HEADERS } from './constants';
import { domain, parseJsonOrThrow, parseNewsAddResponse } from './http';

/** `categoryID` for `newsout.php` — маркетингові новини (головна, вкладки company/marketing). */
export const NEWS_CATEGORY_MARKETING = 3;
/** `categoryID` for `newsout.php` — новини компанії. */
export const NEWS_CATEGORY_COMPANY = 4;

/** Default `categoryID` in `getNews` (загальна стрічка / фільтр без категорії). */
export const NEWS_CATEGORY_DEFAULT = 1;

/** `categoryID` for `newsout.php` — події / календар на головній (`EventSection`). */
export const NEWS_CATEGORY_HOME_EVENTS = 2;

/**
 * `all` for `newsout.php` — заплановані пости (admin «Заплановані»), не categoryID.
 * Виклик: `getNews(NEWS_OUT_ALL_PLANNED)` або з явною категорією.
 */
export const NEWS_OUT_ALL_PLANNED = 2;

function isNewsPostRow(item: unknown): item is NewsPost {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) {
    return false;
  }
  const id = (item as { id?: unknown }).id;
  return id !== undefined && id !== null && id !== '';
}

/**
 * Defensive parse for `newsout.php`: array of posts, or a single post object
 * (some `getNewsFromID` deployments return one object).
 */
export function normalizeNewsPostList(raw: unknown): NewsPost[] {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return isNewsPostRow(raw) ? [raw] : [];
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter(isNewsPostRow);
}

export type GetNewsRequestOptions = {
  limit?: number;
  start?: number;
};

export const getNews = (
  all = 0,
  categoryID = NEWS_CATEGORY_DEFAULT,
  request?: GetNewsRequestOptions
): Promise<NewsPost[]> => {
  const token = getCookie('_form_token');
  const body: Record<string, unknown> = {
    limit: request?.limit ?? 100,
    all,
    categoryID,
    token,
  };
  if (request?.start !== undefined) {
    body.start = request.start;
  }
  return fetch(`${domain}/php/newsout.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify(body),
  })
    .then((response) => parseJsonOrThrow<unknown>(response))
    .then(normalizeNewsPostList);
};

export const getNewsFromID = (
  postID: EntityId,
  debug = 0
): Promise<NewsPost[]> => {
  const token = getCookie('_form_token');
  return fetch(`${domain}/php/newsout.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify({ id: postID, token, debug }),
  })
    .then((response) => parseJsonOrThrow<unknown>(response))
    .then(normalizeNewsPostList);
};

export const sendCommentNews = (
  newsID: EntityId,
  userID: EntityId,
  text: string
): Promise<unknown> => {
  const token = getCookie('_form_token');
  return fetch(`${domain}/php/commentadd.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify({ newsID, userID, text, token }),
  }).then((response) => parseJsonOrThrow<unknown>(response));
};

export const toggleLike = (
  newsID: EntityId,
  userID: EntityId,
  code: string
): Promise<Response> => {
  const token = getCookie('_form_token');
  return fetch(`${domain}/php/reactionsadd.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify({ newsID, userID, code, token }),
  });
};

export interface CreateNewPostParams {
  title: string;
  tags: string | string[];
  date: string;
  text: string;
  imgFile: File[] | Blob[];
  cat_id: EntityId;
  autor_id: EntityId | undefined;
  html?: string;
}

export const createNewPost = async ({
  title,
  tags,
  date,
  text,
  imgFile,
  cat_id,
  autor_id,
  html,
}: CreateNewPostParams): Promise<unknown> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('title', title);
  data.append('tags', Array.isArray(tags) ? tags.join(',') : tags);
  data.append('pub_date', date);
  data.append('text', text);
  if (html) data.append('html', html);
  data.append('cat_id', String(cat_id));
  data.append('autor_id', String(autor_id ?? ''));
  for (let i = 0; i < imgFile.length; i++) {
    data.append('img[]', imgFile[i]!);
  }
  const response = await fetch(`${domain}/php/newsadd.php`, {
    method: 'POST',
    body: data,
  });
  return parseJsonOrThrow<unknown>(response);
};

export interface UpdateNewsPostParams {
  title: string;
  tags: string | string[];
  date: string;
  text: string;
  imgFile: File[] | Blob[];
  cat_id: EntityId;
  autor_id: EntityId | undefined;
  id: EntityId | undefined;
  delimg: string | string[];
  html?: string;
}

export const updateNewsPost = ({
  title,
  tags,
  date,
  text,
  imgFile,
  cat_id,
  autor_id,
  id,
  delimg,
  html,
}: UpdateNewsPostParams): Promise<unknown> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('id', String(id ?? ''));
  data.append('token', `${token}`);
  data.append('title', title);
  data.append('tags', Array.isArray(tags) ? tags.join(',') : tags);
  data.append('pub_date', date);
  data.append('text', text);
  if (html) data.append('html', html);
  data.append('delimg', Array.isArray(delimg) ? delimg.join(',') : delimg);
  data.append('cat_id', String(cat_id));
  data.append('autor_id', String(autor_id ?? ''));
  for (let i = 0; i < imgFile.length; i++) {
    data.append('img[]', imgFile[i]!);
  }
  return fetch(`${domain}/php/newsadd.php`, {
    method: 'POST',
    body: data,
  }).then((response) => parseNewsAddResponse(response));
};

export const deleteNewsPost = (
  newsID: EntityId,
  userID: EntityId,
  action = 'news'
): Promise<Response> => {
  const token = getCookie('_form_token');
  return fetch(`${domain}/php/delete.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify({
      id: newsID,
      action,
      userID,
      token,
    }),
  });
};

export const deleteProduct = (
  productID: EntityId | undefined,
  userID: EntityId | undefined,
  action = 'news'
): Promise<Response> => {
  const token = getCookie('_form_token');
  return fetch(`${domain}/php/delete.php`, {
    method: 'POST',
    headers: JSON_POST_HEADERS,
    body: JSON.stringify({
      id: productID,
      action,
      userID,
      token,
    }),
  });
};
